import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { useTheme } from '@mui/material/styles'
import { Box, Button, Dialog, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material'

import useMarkets from 'hooks/useMarkets'
import useUser from 'hooks/useUser'
import { detectMarket, getMarketLabel } from 'shared/functions/detectMarket'
import { SET_CURRENT_USER } from 'store/actions'
import MarketFlag from 'ui-component/MarketFlag'

import logo from 'assets/icons/content-express.svg'

// ==============================|| MARKET DETECTION MODAL ||============================== //

const assignOffice = ({ currentUser, dispatch, office, t, updateUser }) =>
  updateUser({ id: currentUser.id, office }).then(({ success, errors }) => {
    if (!success) {
      toast.warn(errors?.[0] || t('market.saveError'))
      return false
    }

    dispatch({ type: SET_CURRENT_USER, currentUser: { ...currentUser, office } })
    window.location.reload()
    return true
  })

const MarketDetectionModal = () => {
  const { t } = useTranslation('modals')
  const theme = useTheme()
  const dispatch = useDispatch()

  const { currentUser } = useSelector((state) => state.customization)

  const {
    callbacks: { updateUser },
    updating,
  } = useUser()

  const { defaultMarket, getMarket, loaded, markets } = useMarkets()
  const [chosenMarket, setSelectedMarket] = useState(null)
  const autoSaving = useRef(false)

  // Markets load async, so detect lazily until the user picks one.
  const selectedMarket = chosenMarket || detectMarket(markets, defaultMarket)

  const enabledMarkets = markets.filter((market) => market.Enabled)
  const open = !!currentUser && !currentUser.office && loaded && enabledMarkets.length > 1

  const saveMarket = (marketCode) => {
    const market = getMarket(marketCode)
    const office = market?.OfficeId

    if (!office || !market.Enabled) {
      toast.warn(t('market.invalidMarket'))
      return
    }

    return assignOffice({ currentUser, dispatch, office, t, updateUser })
  }

  const save = () => saveMarket(selectedMarket)

  useEffect(() => {
    if (!loaded || !currentUser || currentUser.office || autoSaving.current) return

    const enabled = markets.filter((market) => market.Enabled)
    if (enabled.length !== 1) return

    const office = enabled[0].OfficeId
    if (!office) return

    autoSaving.current = true
    assignOffice({ currentUser, dispatch, office, t, updateUser })
  }, [currentUser, dispatch, loaded, markets, t, updateUser])

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            p: 3,
            outline: 'none',
            '&:focus, &:focus-visible': { outline: 'none' },
          },
        },
      }}
    >
      <Stack
        spacing={2}
        sx={{
          alignItems: 'center',
          textAlign: 'center',
          pt: 1,
          '& *:focus, & *:focus-visible': { outline: 'none' },
        }}
      >
        <Box component="img" src={logo} alt="Content Express" sx={{ width: 72, maxWidth: '50%' }} />
        <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.primary.dark }}>
          {t('market.welcome')}
        </Typography>
        <Typography color="text.secondary" sx={{ lineHeight: '1.25rem' }}>
          {t('market.currencyQuestion')}
        </Typography>
        <FormControl sx={{ minWidth: 225 }} disabled={!loaded}>
          <InputLabel id="market-select-label">{t('market.market')}</InputLabel>
          <Select
            labelId="market-select-label"
            label={t('market.market')}
            value={loaded ? selectedMarket : 'loading'}
            onChange={(e) => setSelectedMarket(e.target.value)}
            size="small"
            renderValue={(selected) =>
              selected === 'loading' ? (
                <Stack direction="row">{t('market.loading')}</Stack>
              ) : (
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                  <MarketFlag market={selected} />
                  {getMarketLabel(getMarket(selected)) || selected}
                </Stack>
              )
            }
          >
            {!loaded && <MenuItem value="loading">{t('market.loading')}</MenuItem>}
            {enabledMarkets.map((market) => (
              <MenuItem key={market.Code} value={market.Code}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                  <MarketFlag market={market.Code} />
                  {getMarketLabel(market)}
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          loading={updating || !loaded}
          variant="contained"
          color="secondary"
          disableElevation
          onClick={save}
          sx={{ borderRadius: 6, px: 5, py: 1, textTransform: 'none', fontWeight: 600 }}
        >
          {t('market.confirm')}
        </Button>
      </Stack>
    </Dialog>
  )
}

export default MarketDetectionModal
